import { decode as msgPackDecode } from '@msgpack/msgpack/dist/decode';
import moment from 'moment/moment';

import configLib from '../../../configLib';
import { recordPerfMetric } from '../../debug/performance';
import { PredefinedMilisecondsTypeEnum, WorkerInMessageType, WorkerOutMessageType } from '../../enums';
import { HttpStatusCodeEnum } from '../../enums/http-status-code.enum';
import { setSlowConnectionStatus } from '../../store/app';
import GatewayWebsocketState from '../../store-util/GatewayWebsocketState';
import Lz4StatsState from '../../store-util/Lz4StatsState';
import { sleep } from '../../util/CommonHelpers';
import { lz4Unpickle } from '../../util/GatewayHelpers';
import { applyGatewayRequestSymbology } from '../../util/MarketDataHelpers';
import { uuidv4 } from '../../util/tools';
import { isEmpty, isString } from '../../util/typeGuards';
import { NullableTimeout } from '../../util/types';
import { WebSocketEventData, WebSocketEventTypes } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { BaseSubscriptionRequestType } from '../subscription/types';

import {
  GW_NON_RESTORABLE_MESSAGE_TYPES,
  GW_TRANSIENT_RESPONSE_RETRY_LIMIT,
  GW_TRANSIENT_RESPONSE_RETRYABLE_RESULT_CODES,
  MANUAL_DISCONNECT_REASON,
  MANUAL_RECONNECT_REASON,
  LATE_REQUEST_RELOG_INTERVAL_MS,
  LATE_REQUEST_SWEEP_INTERVAL_MS,
  LATE_REQUEST_THRESHOLD_MS,
  LATE_REQUEST_TRACKED_MESSAGE_TYPES,
  RECOVERY_RECONNECT_DELAY_MS,
  RECOVERY_RECONNECT_REASONS,
  REMOTE_CLOSE_REASON,
  WEB_SOCKET_CONNECTION_TRY_COUNT,
  WEB_SOCKET_MAX_SUCCESS_STATUS,
} from './constants';
import { GatewayRequestType, GatewayResponseType, KeepAliveMode } from './types';

const { WebSocket: WebSocketImpl, Response } = configLib;

const LOG_PREFIX = '[BASE-WS]';
const logger = LoggerService.getLogger();

export abstract class BaseWebSocketService<T extends BaseSubscriptionRequestType> {
  private readonly responseListeners: Map<
    GatewayResponseType,
    Set<{ listener: (event: WebSocketEventData<any>) => void; filter?: (event: WebSocketEventData<any>) => boolean }>
  > = new Map();

  private webSocket?: WebSocket | null;

  private readonly loginTimeout: PredefinedMilisecondsTypeEnum;

  private loginTimeoutId: NullableTimeout;

  private lateRequestSweepIntervalId: NullableTimeout;

  private consecutiveRecoveryFailureCount = 0;

  private readonly transientResponseRetries = new Map<string, number>();

  /**
   * Set when the app asks for the close. `disconnect()` only nulls `webSocket` when the socket is
   * already OPEN, so the `this.webSocket !== webSocket` guard alone does not cover a disconnect
   * issued while the socket is still CONNECTING - that close would otherwise look remote and
   * trigger a reconnect the app never wanted.
   */
  private pendingCloseReason?: string;

  private readonly onWorkerMessage = (event: MessageEvent): void => {
    const { data } = event;

    if (data === WorkerOutMessageType.PingTick) {
      if (!this.isLoggedin) return;

      const traceIdentifier = uuidv4();
      const timeout = PredefinedMilisecondsTypeEnum.oneSecond;
      this.sendRequest({
        messageType: GatewayRequestType.Ping,
        traceIdentifier,
        keepAliveMode: KeepAliveMode.Application,
      } as T);
      configLib.worker?.postMessage({ type: WorkerInMessageType.SchedulePongTimeout, traceIdentifier, timeout });
      return;
    }

    if (data?.type === WorkerOutMessageType.PongTimeout) {
      this.removeRequestedObjectByTraceIdentifier(data.traceIdentifier);
      configLib.store.dispatch(setSlowConnectionStatus(true));
    }
  };

  private isLoggedin = false;

  private isReconnecting = false;

  private connectionTryCount = 0;

  private requestQueue: T[] = [];

  private workerAbortController?: AbortController;

  private requestedObjects: T[] = []; // We store requests here with traceIdentifier data to find the relation between the response and the request

  constructor(loginTimeout: PredefinedMilisecondsTypeEnum) {
    this.loginTimeout = loginTimeout;
  }

  public async connect(tryCount = WEB_SOCKET_CONNECTION_TRY_COUNT) {
    if (this.connectionTryCount >= tryCount) {
      logger.warn(`${LOG_PREFIX} max reconnection count exceeded! Please check your connection:`, this.getWebSocketState());
      return;
    }

    if (this.connectionTryCount > 0) {
      // if it's not the first time connection try, wait and try
      await sleep(PredefinedMilisecondsTypeEnum.oneSecond);
    }

    this.connectionTryCount++;  
    if (this.isConnected() && this.isAuthenticated()) {
      logger.warn(
        `${LOG_PREFIX} Reconnect will be canceled! it's already been connected and authenticated. state:`,
        this.getWebSocketState(),
      );
      return;
    }

    if (this.isBusy()) {
      logger.warn(`${LOG_PREFIX} Connection is CLOSING/OPENING! Please try again to connect later.state:`, this.getWebSocketState());
      return;
    }

    if (this.isConnected()) {
      logger.warn(`${LOG_PREFIX} WS connection was already established! state:`, this.getWebSocketState());
      return;
    }

    if (this.isConnecting()) {
      logger.warn(`${LOG_PREFIX} Connection attempt already in progress, skipping duplicate request`);
      return;
    }

    logger.info(`${LOG_PREFIX} creating new WS connection. state:`, this.getWebSocketState());
    const webSocket = new WebSocketImpl(this.getBaseUrl());
    this.webSocket = webSocket;
    this.checkLoginStatus();
    this.sendPingRequestPeriodically();

    // TODO: Cover this with integration test ART-4281
    this.webSocket.onopen = (): void => {
      if (this.webSocket !== webSocket) return;
      this.pendingCloseReason = undefined;
      this.resetTryCountToDefault();
    };

    // TODO: Cover this with integration test ART-4281
    this.webSocket.onmessage = (response: any) => {
      if (this.webSocket !== webSocket) return;
      try {
        this.handleGWWebsocketMessages(response);
      } catch (error) {
        logger.error(`${LOG_PREFIX} Can not handle WebSocket response! error:`, error);
      }
    };

    // TODO: Cover this with integration test ART-4281
    // A deliberate disconnect() nulls this.webSocket before the event fires, so the guard above
    // means this body only runs when the gateway dropped us - worth recovering from.
    this.webSocket.onclose = async (event: any): Promise<void> => {
      const closeReason = this.pendingCloseReason ?? REMOTE_CLOSE_REASON;
      this.pendingCloseReason = undefined;
      if (this.webSocket !== webSocket) return;
      logger.warn(`${LOG_PREFIX} WebSocket connection closed! reason:'${closeReason}'`);
      this.webSocket = null; // Give an opportunity to Garbage Collector to remove this instance
      this.isLoggedin = false;
      GatewayWebsocketState.setAll({ isLoggedin: false, isConnected: false, isConnectionTimedOut: false });
      this.teardownConnectionMonitors();

      if (closeReason !== REMOTE_CLOSE_REASON) {
        return; // the app asked for this close
      }

      try {
        await this.reconnect(REMOTE_CLOSE_REASON);
      } catch (error) {
        logger.error(`${LOG_PREFIX} Failed to recover from WebSocket close! error:`, error);
      }
    };

    // TODO: Cover this with integration test ART-4281
    this.webSocket.onerror = async (event: any): Promise<void> => {
      if (this.webSocket !== webSocket) return;
      logger.error(`${LOG_PREFIX} An error occurred on WebSocket connection create!, event:`, event);
      this.isLoggedin = false;
      this.webSocket = null; // Give an opportunity to Garbage Collector to remove this instance
      GatewayWebsocketState.setAll({ isLoggedin: false, isConnected: false, isConnectionTimedOut: false });

      this.teardownConnectionMonitors();

      try {
        // reconnect(), not connect(): only reconnect replays the active subscriptions, and login
        // clears requestedObjects, so a plain connect() would come back with nothing subscribed.
        await this.reconnect('socket_error');
      } catch (error) {
        logger.error(`${LOG_PREFIX} Failed to recover from WebSocket error! error:`, error);
      }
    };
  }

  public resetTryCountToDefault(): void {
    this.connectionTryCount = 0;
  }

  public clearRequestQueue(): void {
    this.requestQueue = [];
  }

  public subscribeToResponses(
    messageType: GatewayResponseType,
    listener: (event: WebSocketEventData<any>) => void,
    filter?: (event: WebSocketEventData<any>) => boolean,
  ): () => void {
    const listeners = this.responseListeners.get(messageType) ?? new Set();
    listeners.add({ listener, filter });
    this.responseListeners.set(messageType, listeners);

    return () => {
      const currentListeners = this.responseListeners.get(messageType);
      currentListeners?.forEach(entry => {
        if (entry.listener === listener) {
          currentListeners.delete(entry);
        }
      });

      if (currentListeners?.size === 0) {
        this.responseListeners.delete(messageType);
      }
    };
  }

  private notifyResponseListeners(messageType: GatewayResponseType, event: WebSocketEventData<any>) {
    this.responseListeners.get(messageType)?.forEach(({ listener, filter }) => {
      if (!filter || filter(event)) {
        listener(event);
      }
    });
  }

  async handleGWWebsocketMessages(response: any): Promise<void> {
    let { data: dataRaw } = response;
    const isStringResponse = isString(dataRaw);

    if (!isStringResponse && configLib.isWeb) {
      dataRaw = await new Response(dataRaw).arrayBuffer(); // Just in case, this logic was moved from the old implementation
    }

    const responseData = this.getResponseData(isStringResponse, dataRaw);

    const { messageType, resultCode, httpCode } = responseData;
    const isSuccess = this.isSuccessResponse(resultCode, httpCode);
    if (Array.isArray(responseData)) {
      const event = {
        data: {
          arrayData: responseData,
          messageType: responseData[0],
          messageSubType: responseData[1][0],
        },
        eventType: WebSocketEventTypes.PARSED_RESPONSE,
        key: uuidv4(),
      } as WebSocketEventData<any>;
      this.notifyResponseListeners(responseData[0], event);

      return;
    }

    this.handleLoginResponse(isStringResponse, messageType, isSuccess);
    if (this.handlePongResponse(messageType, responseData.traceIdentifier)) {
      return;
    }

    if (await this.handleTransientRetryResponse(responseData)) {
      return;
    }

    const event = {
      data: responseData,
      eventType: WebSocketEventTypes.PARSED_RESPONSE,
      key: responseData.traceIdentifier,
    } as WebSocketEventData<any>;
    this.notifyResponseListeners(messageType, event);
  }

  private removeRequestedObjectByTraceIdentifier(traceIdentifier: string): T | undefined {
    let requestItem: T | undefined;

    this.requestedObjects = this.requestedObjects.filter(item => {
      if (item.traceIdentifier === traceIdentifier) {
        requestItem = item;
        return false;
      }

      return true;
    });

    return requestItem;
  }

  private handlePongResponse(messageType: GatewayResponseType, traceIdentifier?: string): boolean {
    if (messageType === GatewayResponseType.Pong) {
      logger.debug(`${LOG_PREFIX} Pong received`);
      if (traceIdentifier) {
        configLib.worker?.postMessage({ type: WorkerInMessageType.ClearPongTimeout, traceIdentifier });
        const requestItem = this.removeRequestedObjectByTraceIdentifier(traceIdentifier);

        if (requestItem && (requestItem as any).requestTime) {
          const latency = moment.utc().diff((requestItem as any).requestTime);
          if (latency > PredefinedMilisecondsTypeEnum.halfSecond) {
            configLib.store.dispatch(setSlowConnectionStatus(true));
          } else {
            configLib.store.dispatch(setSlowConnectionStatus(false));
          }
        }
      }
      return true;
    }
    return false;
  }

  private handleLoginResponse(isStringResponse: boolean, messageType: GatewayResponseType, isSuccess: boolean) {
    if (isStringResponse && messageType === GatewayResponseType.Login) {
      if (isSuccess) {
        this.isLoggedin = true;
        GatewayWebsocketState.setAll({ isLoggedin: true, isConnected: true, isConnectionTimedOut: false });
        // Clear requestedObjects before sending queue to avoid duplicates
        this.consecutiveRecoveryFailureCount = 0;
        this.requestedObjects = [];
        this.startLateRequestSweeper();
        this.sendAllQueuedRequest();
      } else {
        this.disconnect();
      }
    }
  }

  public isConnecting = (): boolean => this.webSocket?.readyState === WebSocketImpl.CONNECTING;

  public isConnected = (): boolean => this.webSocket?.readyState === WebSocketImpl.OPEN;

  public isClosed = (): boolean => this.webSocket?.readyState === WebSocketImpl.CLOSED;

  public isBusy = (): boolean => !!this.webSocket && !this.isConnected() && !this.isClosed();

  public isAuthenticated = (): boolean => this.isConnected() && this.isLoggedin;

  public async reconnect(reason: string = MANUAL_RECONNECT_REASON) {
    if (this.isReconnecting) {
      logger.warn(`${LOG_PREFIX} Reconnect ignored! Already reconnecting.`);
      return;
    }

    if (this.isConnected() && this.isAuthenticated()) {
      logger.warn(
        `${LOG_PREFIX} Reconnect ignored! Socket is already connected and authenticated.`,
        this.getWebSocketState(),
      );
      return;
    }

    this.isReconnecting = true;
    logger.debug(`${LOG_PREFIX} Socket connection reconnecting... currentStatus:`, this.getWebSocketState());
    this.disconnect();
    this.resetTryCountToDefault();

    // Copy all active subscriptions to requestQueue for re-sending after reconnection
    const subscriptionsToRestore = this.requestedObjects.filter(item => this.isRestorableSubscription(item));

    // Dedupe on the request's content: every trace identifier is unique, so comparing those would
    // never match and the same subscription would be queued again on every reconnect.
    const queuedSubscriptionKeys = new Set(
      this.requestQueue.map(queued => BaseWebSocketService.getRestorableSubscriptionKey(queued)),
    );
    const uniqueSubscriptions = subscriptionsToRestore.filter(item => {
      const subscriptionKey = BaseWebSocketService.getRestorableSubscriptionKey(item);
      if (queuedSubscriptionKeys.has(subscriptionKey)) {
        return false;
      }

      queuedSubscriptionKeys.add(subscriptionKey);
      return true;
    });

    // A restored request is a new request - reusing the id would collide with the pre-reconnect one.
    uniqueSubscriptions.forEach(subscription => { subscription.traceIdentifier = uuidv4(); });

    this.requestQueue.push(...uniqueSubscriptions);
    logger.log(
      `${LOG_PREFIX} Restoring ${uniqueSubscriptions.length} subscription(s) after reconnect (total queue: ${this.requestQueue.length})`,
    );

    const reconnectDelayMs = this.getRecoveryReconnectDelay(reason);
    logger.log(`${LOG_PREFIX} Reconnecting in ${reconnectDelayMs}ms (reason:'${reason}')`);

    try {
      await sleep(reconnectDelayMs);
      await this.connect();
    } finally {
      this.isReconnecting = false;
    }
  }

  public disconnect(reason: string = MANUAL_DISCONNECT_REASON): void {
    logger.warn(`${LOG_PREFIX} Socket connection disconnecting... currentStatus:`, this.getWebSocketState());
    configLib.store?.dispatch(setSlowConnectionStatus(false));

    // A socket that is still CONNECTING is not `isConnected()`, but it is very much alive - leaving
    // it would let it finish connecting after the app asked to disconnect.
    const currentSocket = this.webSocket;
    const shouldCloseSocket = !!currentSocket
      && currentSocket.readyState !== WebSocketImpl.CLOSED
      && currentSocket.readyState !== WebSocketImpl.CLOSING;

    if (this.isConnected() || shouldCloseSocket) {
      this.pendingCloseReason = reason; // claim the close so onclose does not read it as remote
      this.isLoggedin = false;
      GatewayWebsocketState.setAll({ isLoggedin: false, isConnected: false, isConnectionTimedOut: false });
      if (shouldCloseSocket) {
        currentSocket.close();
      }
      this.webSocket = null;
    }

    this.teardownConnectionMonitors(); // Always clear timers to prevent leaks, regardless of connection state
  }

  private teardownConnectionMonitors(): void {
    if (this.lateRequestSweepIntervalId) {
      clearInterval(this.lateRequestSweepIntervalId as NodeJS.Timeout);
      this.lateRequestSweepIntervalId = undefined;
    }

    if (this.loginTimeoutId) {
      clearTimeout(this.loginTimeoutId as NodeJS.Timeout);
      this.loginTimeoutId = undefined;
    }

    configLib.worker?.postMessage({ type: WorkerInMessageType.StopPingInterval });
    this.workerAbortController?.abort();
    this.workerAbortController = undefined;
  }

  public sendRequest(requestBody: T): void {
    if (!this.isConnected()) {
      logger.warn(`${LOG_PREFIX} Message can not be sent, no WS connection! Data:`, {
        requestBody,
        status: this.webSocket?.readyState,
      });

      if (requestBody.messageType !== GatewayRequestType.Login) {
        this.requestQueue.push(requestBody);
      }

      this.connect();
      return;
    }

    if (requestBody.messageType !== GatewayRequestType.Login && !this.isAuthenticated()) {
      logger.warn(`${LOG_PREFIX} Message can not be sent, no authentication! Data:`, requestBody);
      this.requestQueue.push(requestBody);
      this.connect();

      return;
    }

    if (requestBody.messageType === GatewayRequestType.None) {
      logger.warn(`${LOG_PREFIX} Message can not be sent, no messageType! Data:`, requestBody);
      return;
    }

    this.sendAsString(requestBody);
  }

  public getRequestedObjectList(): T[] {
    return this.requestedObjects;
  }

  /**
   * A socket that dropped on its own is likely to drop again, so each consecutive recovery waits
   * longer. A manual reconnect is a user-visible action and keeps the short delay.
   */
  private getRecoveryReconnectDelay(reason: string): number {
    if (!RECOVERY_RECONNECT_REASONS.has(reason)) {
      return PredefinedMilisecondsTypeEnum.oneSecond;
    }

    this.consecutiveRecoveryFailureCount += 1;
    const delayIndex = Math.min(
      this.consecutiveRecoveryFailureCount - 1,
      RECOVERY_RECONNECT_DELAY_MS.length - 1,
    );

    return RECOVERY_RECONNECT_DELAY_MS[delayIndex];
  }

  /**
   * A busy or timed-out gateway refuses a request without the subscription ever being established,
   * and nothing else would re-send it. Returns true when the request has been re-sent, so the
   * caller stops treating this response as a real answer.
   */
  private async handleTransientRetryResponse(
    responseData: { traceIdentifier?: string; resultCode?: number },
  ): Promise<boolean> {
    const { traceIdentifier, resultCode } = responseData;

    if (!traceIdentifier || !GW_TRANSIENT_RESPONSE_RETRYABLE_RESULT_CODES.has(resultCode as HttpStatusCodeEnum)) {
      return false;
    }

    const requestItem = this.requestedObjects.find(item => item.traceIdentifier === traceIdentifier);
    if (!requestItem || GW_NON_RESTORABLE_MESSAGE_TYPES.has(requestItem.messageType)) {
      return false;
    }

    const retryCount = this.transientResponseRetries.get(traceIdentifier) ?? 0;
    if (retryCount >= GW_TRANSIENT_RESPONSE_RETRY_LIMIT) {
      logger.warn(`${LOG_PREFIX} transient response retry limit reached`, {
        traceIdentifier,
        resultCode,
        retryCount,
      });
      return false;
    }

    this.transientResponseRetries.set(traceIdentifier, retryCount + 1);
    // Reset the clock so the late-request sweeper measures from this attempt, not the first one
    requestItem.requestTime = moment.utc();
    requestItem.respondedAt = undefined;

    await sleep(PredefinedMilisecondsTypeEnum.oneSecond);

    logger.warn(`${LOG_PREFIX} retrying request after transient gateway response`, {
      traceIdentifier,
      resultCode,
      attempt: retryCount + 1,
    });

    const retryRequest: T = { ...requestItem };
    delete retryRequest.optionalParams;
    delete retryRequest.requestedSymbols;
    delete retryRequest.requestTime;
    delete retryRequest.respondedAt;
    delete retryRequest.lateLoggedAt;
    this.webSocket?.send(JSON.stringify(applyGatewayRequestSymbology(retryRequest)));

    return true;
  }

  private isRestorableSubscription(item: T): boolean {
    if (GW_NON_RESTORABLE_MESSAGE_TYPES.has(item.messageType)) {
      return false;
    }

    // A subscription whose symbols have all been unsubscribed has nothing left to restore
    const symbols = (item as any).symbols;
    return !(Array.isArray(symbols) && isEmpty(symbols));
  }

  /** Identity of a subscription for dedupe purposes: its content, minus per-request bookkeeping. */
  private static getRestorableSubscriptionKey(item: BaseSubscriptionRequestType): string {
    const subscriptionData = { ...(item as any) };
    delete subscriptionData.traceIdentifier;
    delete subscriptionData.optionalParams;
    delete subscriptionData.requestedSymbols;
    delete subscriptionData.requestTime;
    delete subscriptionData.respondedAt;
    delete subscriptionData.lateLoggedAt;

    return BaseWebSocketService.stableStringify(subscriptionData);
  }

  /** Key order and symbol order must not change the identity, so both are sorted. */
  private static stableStringify(value: any): string {
    if (Array.isArray(value)) {
      return JSON.stringify([ ...value ].sort());
    }

    if (value && typeof value === 'object') {
      const sortedValue = Object.keys(value).sort().reduce((accumulator, key) => ({
        ...accumulator,
        [key]: BaseWebSocketService.normalizeValue(value[key]),
      }), {});

      return JSON.stringify(sortedValue);
    }

    return JSON.stringify(value);
  }

  private static normalizeValue(value: any): any {
    if (Array.isArray(value)) {
      return [ ...value ].sort();
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).sort().reduce((accumulator, key) => ({
        ...accumulator,
        [key]: BaseWebSocketService.normalizeValue(value[key]),
      }), {});
    }

    return value;
  }

  protected abstract getBaseUrl(): string;

  private sendAsString(requestBody: T): void {
    const symbols = (requestBody as any).symbols;
    this.requestedObjects.push({
      ...requestBody,
      // Snapshot of what this request asked for; the caller's `symbols` array keeps growing as the
      // subscription does, so it cannot be used to tell which symbols this response should carry.
      requestedSymbols: Array.isArray(symbols) ? [ ...symbols ] : undefined,
      requestTime: moment.utc(),
      respondedAt: undefined,
      lateLoggedAt: undefined,
    });
    const requiredParams: T = { ...requestBody };
    // FE-only bookkeeping - the backend has no use for any of it
    delete requiredParams.optionalParams;
    delete requiredParams.requestedSymbols;
    delete requiredParams.requestTime;
    delete requiredParams.respondedAt;
    delete requiredParams.lateLoggedAt;
    this.webSocket?.send(JSON.stringify(applyGatewayRequestSymbology(requiredParams)));
  }

  /** Reports subscription requests the gateway has not answered, so silent drops are visible. */
  private startLateRequestSweeper(): void {
    if (this.lateRequestSweepIntervalId) {
      return;
    }
    this.lateRequestSweepIntervalId = setInterval(
      () => this.sweepOutstandingRequests(),
      LATE_REQUEST_SWEEP_INTERVAL_MS,
    );
  }

  private sweepOutstandingRequests(): void {
    if (isEmpty(this.requestedObjects)) {
      return;
    }

    const now = moment.utc();

    this.requestedObjects.forEach(item => {
      if (item.respondedAt || !item.requestTime || !LATE_REQUEST_TRACKED_MESSAGE_TYPES.has(item.messageType)) {
        return;
      }

      const elapsedMs = now.diff(item.requestTime);
      if (elapsedMs < LATE_REQUEST_THRESHOLD_MS) {
        return;
      }

      const sinceLastLog = item.lateLoggedAt ? now.diff(item.lateLoggedAt) : Infinity;
      if (sinceLastLog < LATE_REQUEST_RELOG_INTERVAL_MS) {
        return;
      }

      item.lateLoggedAt = now.clone();
      logger.warn(`${LOG_PREFIX} Outstanding request - no response yet`, {
        traceIdentifier: item.traceIdentifier,
        messageType: item.messageType,
        elapsedMs,
        thresholdMs: LATE_REQUEST_THRESHOLD_MS,
        symbols: (item as any).symbols,
        bucket: (item as any).bucket,
        stockExchangeCode: (item as any).stockExchangeCode,
      });
    });
  }

  private readonly isSuccessResponse = (resultCode: any, httpCode: any): boolean => {
    const statusCode = resultCode || httpCode;
    return statusCode <= WEB_SOCKET_MAX_SUCCESS_STATUS;
  };

  private getWebSocketState(): string {
    switch (this.webSocket?.readyState) {
      case WebSocketImpl.OPEN:
        return 'OPEN';
      case WebSocketImpl.CONNECTING:
        return 'CONNECTING';
      case WebSocketImpl.CLOSED:
        return 'CLOSED';
      case WebSocketImpl.CLOSING:
        return 'CLOSING';
      default:
        return 'N/A';
    }
  }

  private readonly getResponseData = (isStringResponse: boolean, dataRaw: any = {}): any => {
    let responseData: any = {};
    if (isStringResponse) {
      try {
        return JSON.parse(dataRaw) || {};
      } catch (error) {
        logger.error(`${LOG_PREFIX} Error parsing JSON message! error:${error}`);
      }

      return responseData;
    }

    // if it is not a String response
    try {
      const uint8Array = new Uint8Array(dataRaw);
      const lz4StartedAt = performance.now();
      const uncompressed = lz4Unpickle(uint8Array);
      const lz4DurationMs = performance.now() - lz4StartedAt;
      const uncompressedBytes = uncompressed.byteLength;
      recordPerfMetric('ws.lz4Unpickle', lz4DurationMs, {
        rawBytes: uint8Array.byteLength,
        uncompressedBytes,
      });
      Lz4StatsState.recordSample({
        durationMs: lz4DurationMs,
        rawBytes: uint8Array.byteLength,
        uncompressedBytes,
      });

      const msgpackStartedAt = performance.now();
      const decoded = msgPackDecode(uncompressed) || {};
      recordPerfMetric('ws.msgpackDecode', performance.now() - msgpackStartedAt);
      return decoded;
    } catch (error) {
      Lz4StatsState.recordFailure(String(error));
      logger.error(`${LOG_PREFIX} decode response failed! error:`, error);
      responseData = dataRaw;
    }

    return responseData;
  };

  private sendAllQueuedRequest() {
    if (isEmpty(this.requestQueue)) {
      // Do nothing because there is no queued request to send!
      return;
    }

    const queueToSend = [ ...this.requestQueue ];
    this.requestQueue = [];

    queueToSend.forEach(item => {
      this.sendRequest(item);
    });
  }

  private sendPingRequestPeriodically() {
    // remove previous listeners
    this.workerAbortController?.abort();
    this.workerAbortController = new AbortController();

    // add new worker listener
    configLib.worker?.addEventListener('message', this.onWorkerMessage, { signal: this.workerAbortController.signal });
    configLib.worker?.postMessage({
      type: WorkerInMessageType.StartPingInterval,
      interval: PredefinedMilisecondsTypeEnum.fiveSeconds,
    });
  }

  private checkLoginStatus() {
    // If there is no point within a given period, disconnect from the WS server!
    this.loginTimeoutId = setTimeout(() => {
      if (this.isConnected() && !this.isLoggedin) {
        // The connection opened but there is no login event!
        this.disconnect();
      }
    }, this.loginTimeout);
  }
}
